import {
  Card,
  Grid,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Divider,
  Typography,
  Box,
  Alert,
  Icon
} from "@mui/material";
import WDBox from "../../../../MaterialComponents/WDBox";
import WDButton from "../../../../MaterialComponents/WDButton";
import WDTypography from "../../../../MaterialComponents/WDTypography";
import AppService from "../../../../Configurations/Appservice";

import { Formik, Form, Field } from "formik";
import { object, string, number, boolean } from "yup";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  dismissLoader,
  showLoader,
  showSuccessMessage,
  updateScreenTitle,
} from "../../../../LayoutContainers/AppLayout/redux/AppSlice";
import { useNavigate } from "react-router-dom";

const initialValues = {
  // Organization Details
  name: "",
  contactPerson: "",
  email: "",
  contactNumber: "",
  address: "",
  commissionReportEmail: "",
  active: true,

  // Commission Configuration
  commissionPercentage: 10,
  commissionType: "PROFIT",

  // Additional Settings
  requiresLogin: false,
  autoSendReports: true,
};

const validationSchema = object({
  name: string().required("Organization name is required"),
  contactPerson: string().optional(),
  email: string().email("Invalid email").optional(),
  contactNumber: string().optional(),
  address: string().required("Address is required"),
  commissionReportEmail: string().email("Invalid email").optional(),
  commissionPercentage: number()
    .required("Commission percentage is required")
    .positive("Must be positive")
    .max(100, "Cannot exceed 100%"),
  commissionType: string().required("Commission type is required"),
});

const ChannelPartnerOnboarding = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isMockMode, setIsMockMode] = useState(false);

  useEffect(() => {
    dispatch(updateScreenTitle("Onboard Channel Partner"));
    return () => dispatch(updateScreenTitle(""));
  }, [dispatch]);

  const _onSubmit = async (values, { resetForm }) => {
    dispatch(showLoader());

    try {
      console.log("Channel Partner Onboarding Data:", values);

      // Step 1: Create the Organization as CHANNEL_PARTNER type
      console.log("Step 1: Creating Channel Partner Organization...");
      const orgResponse = await AppService.post('api/organisations', {
        name: values.name,
        address: values.address,
        contactPerson: values.contactPerson,
        email: values.email,
        contactNumber: values.contactNumber,
        type: 'CHANNEL_PARTNER'
      });

      if (!orgResponse || !orgResponse.id) {
        dispatch(showSuccessMessage("Error: Failed to create organization"));
        return;
      }

      const newOrgId = orgResponse.id;
      console.log("Organization created successfully with ID:", newOrgId);

      // Step 2: Submit Channel Partner Onboarding Application
      console.log("Step 2: Submitting Channel Partner Onboarding Application...");
      const onboardingResponse = await AppService.post('/api/channel-partners/onboarding/apply', {
        organisationId: newOrgId,
        commissionPercentage: values.commissionPercentage,
        commissionType: values.commissionType,
      });

      if (onboardingResponse) {
        dispatch(showSuccessMessage("Channel Partner Created Successfully! Organization has been onboarded."));
        resetForm();
        // Navigate to list after 1.5 seconds
        setTimeout(() => {
          navigate("/ADM-ChannelPartners/adm-channel-partner-list");
        }, 1500);
      } else {
        const errorMsg = onboardingResponse?.message || 'Failed to submit onboarding application';
        console.error("Onboarding API Response:", onboardingResponse);
        dispatch(showSuccessMessage(`Error: ${errorMsg}`));
      }
    } catch (error) {
      console.error("Error creating channel partner:", error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to create channel partner';
      dispatch(showSuccessMessage(`Error: ${errorMsg}`));
    } finally {
      dispatch(dismissLoader());
    }
  };

  return (
    <WDBox py={1}>
      {isMockMode && (
        <Alert severity="warning" icon={<Icon>info</Icon>} sx={{ mb: 2 }}>
          <strong>Mock Mode:</strong> Form will save data locally. Real API integration pending.
        </Alert>
      )}

      <Card sx={{ padding: 3 }}>
        <Formik
          initialValues={initialValues}
          onSubmit={_onSubmit}
          validationSchema={validationSchema}
        >
          {({ errors, isValid, touched, dirty, values, setFieldValue }) => (
            <Form>
              {/* Section: Organization Details */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Icon>business</Icon>
                  Organization Details
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Field
                      name="name"
                      label="Organization Name *"
                      fullWidth
                      as={TextField}
                      error={Boolean(errors.name) && Boolean(touched.name)}
                      helperText={Boolean(touched.name) && errors.name}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Field
                      name="contactPerson"
                      label="Contact Person"
                      fullWidth
                      as={TextField}
                      error={Boolean(errors.contactPerson) && Boolean(touched.contactPerson)}
                      helperText={Boolean(touched.contactPerson) && errors.contactPerson}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Field
                      name="email"
                      type="email"
                      label="Email"
                      fullWidth
                      as={TextField}
                      error={Boolean(errors.email) && Boolean(touched.email)}
                      helperText={Boolean(touched.email) && errors.email}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Field
                      name="contactNumber"
                      label="Contact Number"
                      fullWidth
                      as={TextField}
                      placeholder="+971 XX XXX XXXX"
                      error={Boolean(errors.contactNumber) && Boolean(touched.contactNumber)}
                      helperText={Boolean(touched.contactNumber) && errors.contactNumber}
                    />
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <Field
                      name="address"
                      label="Address *"
                      fullWidth
                      as={TextField}
                      error={Boolean(errors.address) && Boolean(touched.address)}
                      helperText={Boolean(touched.address) && errors.address}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Field
                      name="commissionReportEmail"
                      type="email"
                      label="Commission Report Email (optional)"
                      fullWidth
                      as={TextField}
                      error={Boolean(errors.commissionReportEmail) && Boolean(touched.commissionReportEmail)}
                      helperText={
                        Boolean(touched.commissionReportEmail) && errors.commissionReportEmail
                          ? errors.commissionReportEmail
                          : "Leave empty to use main email"
                      }
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.active}
                          onChange={(e) => setFieldValue("active", e.target.checked)}
                        />
                      }
                      label="Active"
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Section: Commission Configuration */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Icon>percent</Icon>
                  Commission Configuration
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Field
                      name="commissionPercentage"
                      type="number"
                      label="Commission Percentage *"
                      fullWidth
                      as={TextField}
                      error={Boolean(errors.commissionPercentage) && Boolean(touched.commissionPercentage)}
                      helperText={Boolean(touched.commissionPercentage) && errors.commissionPercentage}
                      InputProps={{
                        endAdornment: <Typography variant="body2" color="text.secondary">%</Typography>
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Field name="commissionType">
                      {({ field }) => (
                        <TextField
                          {...field}
                          select
                          label="Commission Type *"
                          fullWidth
                          helperText="What the commission is calculated on"
                        >
                          <MenuItem value="PROFIT">PROFIT</MenuItem>
                          <MenuItem value="REVENUE">REVENUE</MenuItem>
                        </TextField>
                      )}
                    </Field>
                  </Grid>

                  <Grid item xs={12}>
                    <Alert severity="info" icon={<Icon>info</Icon>}>
                      <span>
                        Commission: {values.commissionPercentage || 0}% on{" "}
                        {values.commissionType === "PROFIT" ? "profit" : "revenue"}.
                        {values.commissionType === "PROFIT"
                          ? " For AED 100 profit, partner earns AED " + (values.commissionPercentage || 0) + "."
                          : " For AED 1000 revenue, partner earns AED " + ((values.commissionPercentage || 0) * 10) + "."}
                      </span>
                    </Alert>
                  </Grid>
                </Grid>
              </Box>

              {/* Section: Additional Settings */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Icon>settings</Icon>
                  Additional Settings
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.requiresLogin}
                          onChange={(e) => setFieldValue("requiresLogin", e.target.checked)}
                        />
                      }
                      label="Requires Login (Dashboard Access)"
                    />
                    <Typography variant="caption" display="block" color="text.secondary">
                      Enable if partner needs access to dashboard
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.autoSendReports}
                          onChange={(e) => setFieldValue("autoSendReports", e.target.checked)}
                        />
                      }
                      label="Auto-Send Monthly Reports"
                    />
                    <Typography variant="caption" display="block" color="text.secondary">
                      Automatically email commission reports
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Actions */}
              <WDBox mt={4} display="flex" gap={2} justifyContent="flex-end">
                <WDButton
                  variant="outlined"
                  color="secondary"
                  onClick={() => navigate("/ADM-ChannelPartners/adm-channel-partner-list")}
                >
                  Cancel
                </WDButton>
                <WDButton
                  type="submit"
                  variant="gradient"
                  color="info"
                  disabled={!isValid || !dirty}
                >
                  Create Channel Partner
                </WDButton>
              </WDBox>
            </Form>
          )}
        </Formik>
      </Card>
    </WDBox>
  );
};

export default ChannelPartnerOnboarding;
